// Extensions > Macros: record the edits made on a sheet and play them back. With relative references the recorded
// cells are taken relative to the cell selected when recording started, so the macro can run anywhere.
import type { Op } from '../../shared/ops.ts';
import type { PanelCtx } from './panels.ts';
import { ruleId } from './rules.ts';
import { dialog, h, toast } from './ui.ts';
import type { MenuItem } from './ui.ts';

export interface Macro { id: string; name: string; key?: number; relative: boolean; steps: Op[] }
export const macrosOf = (x: PanelCtx): Macro[] => (x.model.props.macros ?? []) as Macro[];

interface Recording { sheet: number; r: number; c: number; relative: boolean; steps: Op[]; bar: HTMLElement }
let rec: Recording | null = null;
export const recording = (): boolean => rec !== null;

/** Called with every change the user makes while recording. */
export function capture(op: Op): void {
  if (!rec) return;
  const keep = (o: Op): Op | null => {
    if (o.t === 'batch') { const ops = o.ops.map(keep).filter((p): p is Op => p !== null); return ops.length ? { t: 'batch', ops } : null; }
    if (!('sheet' in o) || o.sheet !== rec!.sheet || o.t.startsWith('sheet.')) return null;   // only edits on the recording sheet
    return JSON.parse(JSON.stringify(o)) as Op;
  };
  const k = keep(op); if (k) rec.steps.push(k);
}
/** Move a recorded change by (dr, dc) and onto `sheet`. */
function shift(o: Op, dr: number, dc: number, sheet: number): Op | null {
  switch (o.t) {
    case 'batch': { const ops = o.ops.map(p => shift(p, dr, dc, sheet)).filter((p): p is Op => p !== null); return ops.length ? { t: 'batch', ops } : null; }
    case 'set': return o.r + dr < 0 || o.c + dc < 0 ? null : { ...o, sheet, r: o.r + dr, c: o.c + dc };
    case 'fmt': return o.r1 + dr < 0 || o.c1 + dc < 0 ? null : { ...o, sheet, r1: o.r1 + dr, r2: o.r2 + dr, c1: o.c1 + dc, c2: o.c2 + dc };
    case 'insert': case 'delete': { const at = o.at + (o.axis === 'row' ? dr : dc); return at < 0 ? null : { ...o, sheet, at }; }
    case 'props': return { ...o, sheet };
    default: return null;
  }
}

export function startRecording(x: PanelCtx, host: HTMLElement): void {
  if (rec) return;
  const relBtn = h('button', { class: 'fv-btn on' }, '상대 참조 사용'), absBtn = h('button', { class: 'fv-btn' }, '절대 참조 사용');
  const setMode = (relative: boolean): void => { if (rec) rec.relative = relative; relBtn.classList.toggle('on', relative); absBtn.classList.toggle('on', !relative); };
  relBtn.onclick = () => setMode(true); absBtn.onclick = () => setMode(false);
  const bar = h('div', { class: 'macro-bar' }, h('span', { class: 'rec-dot' }), h('b', {}, '새 매크로 기록 중…'), absBtn, relBtn, h('div', { class: 'grow' }),
    h('button', { class: 'fv-btn', onclick: () => stopRecording(x, false) }, '취소'), h('button', { class: 'primary', onclick: () => stopRecording(x, true) }, '저장'));
  rec = { sheet: x.sheet(), r: x.grid.sel.r, c: x.grid.sel.c, relative: true, steps: [], bar };
  host.append(bar);
}
async function stopRecording(x: PanelCtx, save: boolean): Promise<void> {
  const r = rec; if (!r) return;
  rec = null; r.bar.remove();
  if (!save) return;
  if (!r.steps.length) { toast('기록된 작업이 없습니다'); return; }
  const used = new Set(macrosOf(x).map(m => m.key).filter(Boolean));
  const name = h('input', { class: 'dlg-input', value: `매크로 ${macrosOf(x).length + 1}` });
  const key = h('select', { class: 'dlg-input' }, h('option', { value: '' }, '단축키 없음'), ...[1, 2, 3, 4, 5, 6, 7, 8, 9, 0].filter(n => !used.has(n)).map(n => h('option', { value: String(n) }, `Ctrl+Alt+Shift+${n}`)));
  if (!await dialog('새 매크로 저장', h('div', { class: 'col' }, h('span', { class: 'pf-label' }, '이름'), name, h('span', { class: 'pf-label' }, '단축키'), key), [{ label: '취소', value: false }, { label: '저장', value: true, primary: true }])) return;
  const steps = r.relative ? r.steps.map(s => shift(s, -r.r, -r.c, 0)).filter((s): s is Op => s !== null) : r.steps;
  const m: Macro = { id: ruleId(), name: name.value.trim() || '매크로', relative: r.relative, steps, ...(key.value ? { key: Number(key.value) } : {}) };
  x.submit({ t: 'docprops', patch: { macros: [...macrosOf(x), m] } });
  toast(`'${m.name}' 매크로를 저장했습니다`);
}
export function runMacro(x: PanelCtx, m: Macro): void {
  if (!x.canEdit()) { toast('보기 전용 문서입니다'); return; }
  const dr = m.relative ? x.grid.sel.r : 0, dc = m.relative ? x.grid.sel.c : 0;
  const ops = m.steps.map(s => (m.relative ? shift(s, dr, dc, x.sheet()) : shift(s, 0, 0, x.sheet()))).filter((s): s is Op => s !== null);
  if (!ops.length) return;
  x.submit({ t: 'batch', ops });
  toast(`'${m.name}' 매크로를 실행했습니다`);
}
export async function manageMacros(x: PanelCtx): Promise<void> {
  const list = macrosOf(x).map(m => ({ ...m }));
  const rows = h('div', { class: 'col' });
  const render = (): void => rows.replaceChildren(...(list.length ? list.map((m, i) => h('div', { class: 'row macro-row' },
    h('input', { class: 'dlg-input', value: m.name, oninput: (e: Event) => { m.name = (e.target as HTMLInputElement).value; } }),
    h('select', { onchange: (e: Event) => { const v = (e.target as HTMLSelectElement).value; if (v) m.key = Number(v); else delete m.key; } }, h('option', { value: '' }, '단축키 없음'), ...[1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map(n => h('option', { value: String(n), selected: m.key === n }, `Ctrl+Alt+Shift+${n}`))),
    h('button', { class: 'pf-btn', title: '삭제', onclick: () => { list.splice(i, 1); render(); } }, '✕'))) : [h('p', { class: 'muted' }, '저장된 매크로가 없습니다.')]));
  render();
  if (!await dialog('매크로 관리', rows, [{ label: '취소', value: false }, { label: '업데이트', value: true, primary: true }])) return;
  x.submit({ t: 'docprops', patch: { macros: list.length ? list : null } });
}
export const macroMenu = (x: PanelCtx, host: HTMLElement): MenuItem[] => [
  { label: recording() ? '매크로 기록 중…' : '매크로 기록', disabled: !x.canEdit() || recording(), action: () => startRecording(x, host) },
  { separator: true },
  ...macrosOf(x).map(m => ({ label: m.name, shortcut: m.key !== undefined ? `Ctrl+Alt+Shift+${m.key}` : undefined, disabled: !x.canEdit(), action: () => runMacro(x, m) })),
  ...(macrosOf(x).length ? [{ separator: true } as MenuItem] : []),
];
